import { useState } from "react";
import { Search, Loader2, MapPin, Zap, Navigation, Phone, Clock, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

const COMMON_OHIO_ZIPS = ["43123", "43215", "43017", "43230", "43147", "43040"];

function levelBadge({ dc_fast_count, level2_count }) {
  if (dc_fast_count && dc_fast_count > 0) {
    return { label: `${dc_fast_count} DC Fast`, color: "#10a37f", bg: "#E6FFF4" };
  }
  if (level2_count && level2_count > 0) {
    return { label: `${level2_count} Level 2`, color: "var(--ssc-blue)", bg: "#E8F0FF" };
  }
  return { label: "Level 1", color: "#6b7c93", bg: "#F4F7FB" };
}

function networkColor(network) {
  if (!network) return "#6b7c93";
  const n = network.toLowerCase();
  if (n.includes("tesla")) return "#cc0000";
  if (n.includes("electrify")) return "#0a8c1a";
  if (n.includes("chargepoint")) return "#006DFF";
  if (n.includes("evgo")) return "#6b3fa0";
  if (n.includes("non-networked")) return "#6b7c93";
  return "var(--ssc-navy)";
}

export default function ChargingStationMap() {
  const [zip, setZip] = useState("");
  const [radius, setRadius] = useState(25);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [err, setErr] = useState("");

  async function lookup(value, miles = radius) {
    const code = (value || zip).trim();
    if (!code) {
      setErr("Enter a 5-digit ZIP code first.");
      return;
    }
    setBusy(true);
    setErr("");
    setResults(null);
    try {
      const { data } = await api.post("/charging-stations", {
        location: code,
        radius: miles,
        limit: 25,
      });
      setResults(data);
      if (!data?.items?.length) {
        setErr(`No EV charging stations found within ${miles} miles of ${code}. Try a larger radius.`);
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't look that up right now. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e) {
    e.preventDefault();
    lookup();
  }

  return (
    <div className="rounded-3xl bg-white border border-[#D9E1EA] overflow-hidden" data-testid="charging-station-map">
      <div className="p-5 md:p-7 bg-gradient-to-br from-[#061426] to-[#0c1230] text-white">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-12 h-12 rounded-2xl bg-[var(--ssc-blue)] flex items-center justify-center">
            <MapPin size={24} />
          </div>
          <div>
            <h3 className="text-2xl md:text-3xl font-black tracking-tight">Find EV Chargers Near You</h3>
            <p className="text-[#cdd9ef] text-sm md:text-base mt-1">
              Real-time public charging stations from the U.S. Dept. of Energy database — Tesla, Electrify America, ChargePoint, EVgo, and more.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="mt-5 flex flex-col sm:flex-row gap-2" data-testid="charging-form">
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{5}"
            placeholder="ZIP code (e.g. 43123)"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
            className="flex-1 px-4 py-3 rounded-xl text-base font-bold text-[var(--ssc-navy)] bg-white outline-none focus:ring-2 focus:ring-[var(--ssc-blue)]"
            data-testid="charging-zip"
          />
          <select
            value={radius}
            onChange={(e) => setRadius(Number(e.target.value))}
            className="px-4 py-3 rounded-xl text-base font-bold text-[var(--ssc-navy)] bg-white outline-none"
            data-testid="charging-radius"
          >
            {[10, 25, 50, 75].map((m) => (
              <option key={m} value={m}>Within {m} mi</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={busy}
            className="ssc-btn ssc-btn-primary"
            data-testid="charging-search"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {busy ? "Searching…" : "Find stations"}
          </button>
        </form>

        <div className="mt-3 flex flex-wrap gap-1.5 items-center text-xs text-[#9bb7ff]" data-testid="charging-quick-zips">
          <span className="text-[#cdd9ef]">Quick:</span>
          {COMMON_OHIO_ZIPS.map((z) => (
            <button
              key={z}
              type="button"
              onClick={() => { setZip(z); lookup(z); }}
              className="px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-white font-bold"
              data-testid={`charging-quick-${z}`}
            >
              {z}
            </button>
          ))}
        </div>
      </div>

      <div className="p-5 md:p-7 min-h-[120px]">
        {err && !busy && (
          <div className="rounded-xl bg-[#FFF4F4] border border-[#FFD7D7] text-[var(--ssc-red)] px-4 py-3 text-sm font-bold" data-testid="charging-error">
            {err}
          </div>
        )}

        {!results && !err && !busy && (
          <p className="text-[#6b7c93] text-sm">
            Enter your ZIP above to see real public EV chargers nearby — usually 5–30 stations within 25 miles of Central Ohio.
          </p>
        )}

        {results?.items?.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="text-sm font-bold text-[var(--ssc-navy)]">
                <span className="text-[var(--ssc-blue)]">{results.count}</span> stations within {results.radius_miles} mi of <span className="text-[var(--ssc-blue)]">{results.location}</span>
              </div>
              <a
                href={`https://www.google.com/maps/search/EV+charging+stations/@${results.lat},${results.lng},11z`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-bold text-[var(--ssc-blue)] inline-flex items-center gap-1 hover:underline"
                data-testid="charging-open-map"
              >
                Open in Google Maps <ExternalLink size={12} />
              </a>
            </div>
            <ul className="grid md:grid-cols-2 gap-3" data-testid="charging-results">
              {results.items.map((s) => (
                <StationCard key={s.id || `${s.name}-${s.street}`} s={s} originLat={results.lat} originLng={results.lng} />
              ))}
            </ul>
            <p className="text-xs text-[#6b7c93] mt-4">
              Source: U.S. Dept. of Energy NREL Alternative Fuels Data Center. Station availability and pricing change in real time — confirm with the network before driving out.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function StationCard({ s, originLat, originLng }) {
  const badge = levelBadge(s);
  const netColor = networkColor(s.network);
  const dirsHref = s.latitude && s.longitude
    ? `https://www.google.com/maps/dir/?api=1&origin=${originLat},${originLng}&destination=${s.latitude},${s.longitude}`
    : `https://www.google.com/maps/search/${encodeURIComponent([s.name, s.street, s.city, s.state].filter(Boolean).join(" "))}`;
  return (
    <li
      className={`rounded-2xl border bg-white p-4 flex flex-col gap-2 ${
        s.is_byers_pinned ? "border-[var(--ssc-blue)] ring-2 ring-[var(--ssc-blue)]/20 shadow-md" : "border-[#D9E1EA]"
      }`}
      data-testid={`charging-station-${s.id || ""}`}
    >
      {s.is_byers_pinned && (
        <div className="-mt-1 -mx-1 mb-1 inline-flex self-start items-center gap-1.5 bg-[var(--ssc-blue)] text-white text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded-full"
             data-testid="charging-byers-pinned">
          <Zap size={11} /> Scott's Home Base · Byers Chevrolet
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="font-extrabold text-[var(--ssc-navy)] text-base leading-tight">{s.name}</div>
        {typeof s.distance_miles === "number" && (
          <span className="shrink-0 text-xs font-black text-[var(--ssc-blue)] tabular-nums">
            {s.distance_miles} mi
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span
          className="px-2 py-0.5 rounded-full font-bold uppercase tracking-wide"
          style={{ color: badge.color, background: badge.bg }}
        >
          <Zap size={11} className="inline -mt-0.5 mr-0.5" /> {badge.label}
        </span>
        {s.network && (
          <span className="px-2 py-0.5 rounded-full font-bold border" style={{ color: netColor, borderColor: netColor }}>
            {s.network}
          </span>
        )}
      </div>
      <div className="text-sm text-[#475569] leading-snug">
        {s.street && <div>{s.street}</div>}
        <div>{[s.city, s.state, s.zip].filter(Boolean).join(", ")}</div>
      </div>
      {(s.open_hours || s.pricing) && (
        <div className="text-xs text-[#6b7c93] space-y-0.5">
          {s.open_hours && (
            <div className="inline-flex items-center gap-1"><Clock size={11} /> {s.open_hours}</div>
          )}
          {s.pricing && <div className="line-clamp-2">{s.pricing}</div>}
        </div>
      )}
      {s.scott_note && (
        <div className="rounded-lg bg-[var(--ssc-blue)]/8 border border-[var(--ssc-blue)]/25 px-2.5 py-1.5 text-[11px] text-[var(--ssc-navy)]"
             data-testid="charging-scott-note">
          <span className="font-extrabold text-[var(--ssc-blue)] mr-1">Scott:</span> {s.scott_note}
        </div>
      )}
      <div className="mt-1 flex gap-2 flex-wrap">
        <a
          href={dirsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="ssc-btn ssc-btn-primary text-xs px-3 py-2 inline-flex items-center gap-1"
          data-testid="charging-directions"
        >
          <Navigation size={12} /> Directions
        </a>
        {s.phone && (
          <a
            href={`tel:${s.phone}`}
            className="ssc-btn ssc-btn-secondary text-xs px-3 py-2 inline-flex items-center gap-1"
            data-testid="charging-call"
          >
            <Phone size={12} /> Call
          </a>
        )}
      </div>
    </li>
  );
}
